// Stan pulpitu wędzarni z widoku bramy (WidokWedzarnia2 - typy z Go). Worker podaje gotowe pola (WS-8): stan pracy
// `praca`, tryb sterowania `ctrl`, czasy w sekundach; APP tylko wyświetla.
import type { DeviceOut } from "../../gen/kontrakt";
import type { WidokWedzarnia2 } from "../../gen/linie/wedzarnia-2";
import { KOMENDY_WEDZARNIA2 } from "../../gen/linie/wedzarnia-2";
import { jestOnline } from "../../ekrany/urzadzenia/obecnosc";
import { t } from "../../i18n/t";
import type { Seria } from "../../uklad/wykres/serie";
import type { OdczytListy } from "../lista";
import { nazwaKodu } from "../opisy";

export const KOMENDY = KOMENDY_WEDZARNIA2;

/** Dwa odczyty na karcie w Urządzeniach (WYGLAD.md #66): komora i sonda (POROWNANIE.md 6.6). */
export function odczytyListy(d: DeviceOut): OdczytListy[] {
  const f = stan(d).w?.fields;
  return [{ etykieta: t("Komora"), wartosc: stopnie(f?.chamber) + " °C" }, { etykieta: t("Sonda"), wartosc: stopnie(f?.meat) + " °C" }];
}

/** Co robi wędzarnia (lista urządzeń): „Wędzi · <przepis albo program>”, „Program zakończony”, w stopie nic. */
export function pracaListy(d: DeviceOut): string {
  const s = stan(d);
  if (s.praca === "koniec") return t("Program zakończony");
  if (s.praca !== "praca") return "";
  const co = s.w?.fields.recipe || (s.manual ? t("tryb ręczny") : nazwaProgramu(s.w));
  return co ? `${t("Wędzi")} · ${co}` : t("Wędzi");
}

/** Kanały wykresu (lines/wedzarnia2/type.go temp(0..3)): komora i wsad oraz ich cele. Kolory z tokenów pakietu - komora
 *  w kolorze linii wędzenia (ciągła, z gradientem pod linią), sonda (przerywana), cele kropkami. Pulpit i Statystyki. */
export const SERIE: Seria[] = [
  // Wędzarnia pokazuje same całe stopnie (właściciel 20.10) - kafelki i dymek wykresu.
  { idx: 0, nazwa: t("Komora"), kolor: "--iot-wedzenie", miejsca: 0, wypelnij: true },
  { idx: 1, nazwa: t("Wsad"), kolor: "--iot-wykres-sonda", styl: "przerywana", miejsca: 0 },
  { idx: 2, nazwa: t("Cel komory"), kolor: "--iot-wedzenie", styl: "cel", miejsca: 0 },
  { idx: 3, nazwa: t("Cel wsadu"), kolor: "--iot-wykres-sonda", styl: "cel", miejsca: 0 },
];
export type NazwaKomendy = keyof typeof KOMENDY.nastawy | keyof typeof KOMENDY.akcje | keyof typeof KOMENDY.zestawy;
export const CZAS_S = KOMENDY.czas_potwierdzenia_s;

export interface Stan {
  w: WidokWedzarnia2 | null;
  online: boolean;
  /** Konto może sterować (rola z bramy) i sztuka jest online. */
  steruje: boolean;
  manual: boolean;
  /** stop - zatrzymany; praca - program albo MANUAL w toku; koniec - program skończony (najpierw STOP). */
  praca: "stop" | "praca" | "koniec" | null;
}

export function stan(d: DeviceOut): Stan {
  // Typ widoku wg `type` (brama oddaje wszystkie pola linii, brak odczytu = null).
  const w = d.widok?.type === "wedzarnia-2" ? (d.widok as unknown as WidokWedzarnia2) : null;
  const online = jestOnline(w?.presence.status);
  const program = w?.settings?.PROG ?? null;
  return {
    w,
    online,
    steruje: online && d.mozna.includes("sterowanie"),
    manual: program === null || program === "MANUAL",
    praca: w?.fields.praca ?? null,
  };
}

/** Nazwa programu na pulpicie: przepis - jego nazwa, inaczej nazwa programu ze słownika linii (WLASNY → „Program
 *  własny”, POROWNANIE.md 2.3). */
export function nazwaProgramu(w: WidokWedzarnia2 | null): string {
  const p = w?.settings?.PROG ?? "";
  if (p === "PRZEPIS" && w?.fields.recipe) return w.fields.recipe;
  return nazwaKodu("wedzarnia-2", p);
}

/** Pełne stopnie na kafelkach (WS-12). */
export function stopnie(v: number | null | undefined): string {
  return v == null ? "-" : String(Math.round(v));
}
